import React, { useState, useEffect } from 'react';
import { 
  collection, 
  doc, 
  setDoc, 
  getDocs, 
  query, 
  orderBy,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  getDoc
} from 'firebase/firestore';
import { getCompanyInfo, getDefaultCompanyInfo } from '../../services/companyInfoService';
import { genererPDFDocumentGenerique, imprimerDocumentGenerique } from '../../services/genericDocumentsPdfService';
import { useDocumentTemplates } from './hooks/useDocumentTemplates';
import { useDocumentOperations } from './hooks/useDocumentOperations';
import { useFormatting } from './hooks/useFormatting';
import { useHTMLConversion } from './hooks/useHTMLConversion';
import DocumentList from './components/DocumentList';
import DocumentViewer from './components/DocumentViewer';
import EditModal from './components/EditModal';
import DeleteConfirmModal from './components/DeleteConfirmModal';
import AddDocumentModal from './components/AddDocumentModal';
import './GenericDocumentsManager.css';

const GenericDocumentsManager = ({ user, db }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [documentContent, setDocumentContent] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editContent, setEditContent] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [documentToDelete, setDocumentToDelete] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);

  // Hooks personnalisés
  const { genericDocumentTypes } = useDocumentTemplates();
  const { 
    loadGenericDocuments, 
    resetDocument, 
    saveEdit, 
    deleteDocument,
    createDocument 
  } = useDocumentOperations({ 
    db, user, documents, setDocuments, setSelectedDocument, 
    setDocumentContent, setIsLoading, genericDocumentTypes 
  });
  const { applySimpleFormat } = useFormatting({ editContent, setEditContent });
  const { convertToFullHTMLPreview } = useHTMLConversion();

  // Charger les documents génériques existants et créer ceux qui manquent
  useEffect(() => {
    loadGenericDocuments();
  }, []);

  // Formater la date pour l'affichage
  const formaterDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR');
  };

  // Afficher un document
  const viewDocument = (document) => {
    setSelectedDocument(document);
    setDocumentContent(document.content);
  };

  // Télécharger le document en HTML
  const downloadDocument = () => {
    const blob = new Blob([documentContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${selectedDocument?.documentName}_${new Date().toISOString().split('T')[0]}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Télécharger le document en PDF
  const downloadPDF = async () => {
    if (!selectedDocument) return;
    
    try {
      setIsLoading(true);
      await genererPDFDocumentGenerique(selectedDocument);
    } catch (error) {
      console.error('Erreur lors de la génération du PDF:', error);
      alert('Erreur lors de la génération du PDF: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Imprimer le document
  const printDocument = async () => {
    if (!selectedDocument) return;
    
    try {
      setIsLoading(true);
      await imprimerDocumentGenerique(selectedDocument);
    } catch (error) {
      console.error('Erreur lors de l\'impression:', error);
      alert('Erreur lors de l\'impression: ' + error.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Gérer la sauvegarde des modifications
  const handleSaveEdit = async (selectedDocument, editContent) => {
    const success = await saveEdit(selectedDocument, editContent);
    if (success) {
      // Recharger les documents pour mettre à jour l'affichage avec forceUpdateSelected = true
      await loadGenericDocuments(true);
    }
    return success;
  };

  // Éditer un document
  const editDocument = async (document) => {
    try {
      setIsLoading(true);
      
      let editableContent = '';
      
      // Si c'est un document HTML complet, extraire juste le contenu principal (sans header/footer)
      if (document.content.includes('<!DOCTYPE html>') || document.content.includes('<html>')) {
        // Extraire le contenu entre le header et le footer
        const headerEnd = document.content.indexOf('</header>');
        const footerStart = document.content.indexOf('<footer>');
        
        if (headerEnd !== -1 && footerStart !== -1) {
          editableContent = document.content.substring(headerEnd + 9, footerStart).trim();
        } else {
          // Fallback: extraire le contenu entre les balises body
          const bodyStart = document.content.indexOf('<body>');
          const bodyEnd = document.content.indexOf('</body>');
          
          if (bodyStart !== -1 && bodyEnd !== -1) {
            const bodyContent = document.content.substring(bodyStart + 6, bodyEnd);
            // Essayer d'extraire le contenu principal en supprimant header et footer
            const headerMatch = bodyContent.match(/<header[^>]*>[\s\S]*?<\/header>/);
            const footerMatch = bodyContent.match(/<footer[^>]*>[\s\S]*?<\/footer>/);
            
            if (headerMatch && footerMatch) {
              editableContent = bodyContent
                .replace(headerMatch[0], '')
                .replace(footerMatch[0], '')
                .trim();
            } else {
              editableContent = bodyContent;
            }
          }
        }
      } else {
        // Si ce n'est pas du HTML complet, utiliser le contenu brut
        editableContent = document.content;
      }
      
      setSelectedDocument(document);
      setEditContent(editableContent);
      setShowEditModal(true);
    } catch (error) {
      console.error('Erreur lors de la préparation de l\'édition:', error);
      // En cas d'erreur, utiliser le contenu brut
      setSelectedDocument(document);
      setEditContent(document.content);
      setShowEditModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Ouvrir la confirmation de suppression
  const confirmDelete = (document) => {
    setDocumentToDelete(document);
    setShowDeleteConfirm(true);
  };

  // Gérer la création d'un nouveau document
  const handleCreateDocument = async (documentData) => {
    const success = await createDocument(documentData);
    if (success) {
      setShowAddModal(false);
    }
    return success;
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* En-tête */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Documents Génériques de la Société</h1>
              <p className="text-gray-600 mt-1">Documents uniques pour bénéficiaires et intervenants - Modification et sauvegarde</p>
            </div>
            <div className="flex items-center space-x-4">
              <button
                onClick={() => setShowAddModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Ajouter un document
              </button>
              <div className="text-sm text-gray-500">
                {formaterDate(new Date())}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar - Liste des documents existants */}
          <div className="lg:col-span-1">
            <DocumentList
              documents={documents}
              selectedDocument={selectedDocument}
              isLoading={isLoading}
              onViewDocument={viewDocument}
              onEditDocument={editDocument}
              onDeleteDocument={confirmDelete}
            />
          </div>

          {/* Main content - Visualisation du document */}
          <div className="lg:col-span-3">
            <DocumentViewer
              selectedDocument={selectedDocument}
              documentContent={documentContent}
              onDownload={downloadDocument}
              onDownloadPDF={downloadPDF}
              onPrint={printDocument}
              onEdit={editDocument}
              onReset={resetDocument}
            />
          </div>
        </div>
      </div>

      {/* Modal d'édition */}
      {showEditModal && (
        <EditModal
          selectedDocument={selectedDocument}
          editContent={editContent}
          setEditContent={setEditContent}
          applySimpleFormat={applySimpleFormat}
          convertToFullHTMLPreview={convertToFullHTMLPreview}
          isLoading={isLoading}
          onSave={handleSaveEdit}
          onClose={() => setShowEditModal(false)}
        />
      )}

      {/* Modal de confirmation de suppression */}
      {showDeleteConfirm && (
        <DeleteConfirmModal
          documentToDelete={documentToDelete}
          isLoading={isLoading}
          onDelete={deleteDocument}
          onClose={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Modal d'ajout de document */}
      {showAddModal && (
        <AddDocumentModal
          onSave={handleCreateDocument}
          onClose={() => setShowAddModal(false)}
          isLoading={isLoading}
        />
      )}
    </div>
  );
};

export default GenericDocumentsManager;
